// @flow
import React, { useState, useRef, useEffect, useMemo } from 'react'
import ScrollTrigger from './scroll-trigger'

interface Props {
  src: string
  alt?: string
  width?: string | number
  height?: string | number
  className?: string
  placeholder?: string
  defaultImg?: string
}

const ImageLoader = ({
  src,
  alt = '',
  className = '',
  width = '',
  height = '',
  // eslint-disable-next-line max-len
  placeholder = 'data:image/gif;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8+vHXfwAJpAPhLJ1EpAAAAABJRU5ErkJggg==',
  defaultImg = '/static/images/cover.png',
}: Props) => {
  const imgRef = useRef(null)
  const [ isViewport, setViewport ] = useState(false)
  const [ source, setSource ] = useState(defaultImg || placeholder)
  const [ isLoaded, setLoaded ] = useState(false)
  const loaded = useRef(false)

  const loadImage = () => {
    const image = new Image()
    image.onload = () => {
      setSource(src)
      setLoaded(true)
      loaded.current = true
    }
    image.onerror = () => {
      setSource(defaultImg)
      setLoaded(true)
      loaded.current = true
    }
    image.src = src
  }

  const onEnter = () => {
    if (loaded.current) {
      return
    }
    if (isLoaded) {
      return
    }
    loadImage()
    setViewport(true)
  }

  useEffect(() => {
    if (!placeholder) {
      return
    }
    if (loaded.current) {
      return
    }
    if (isLoaded) {
      return
    }

    const image = new Image()
    image.onload = () => {
      setSource(placeholder)
    }
    image.onerror = () => {
      setSource(defaultImg)
    }
    image.src = placeholder
  }, [ placeholder ])

  useEffect(() => {
    if (!src) {
      return
    }
    setLoaded(false)
    if (isViewport) {
      loadImage()
    }
  }, [ src ])

  const currentSrc = useMemo(() => {
    if (isLoaded && isViewport) {
      return source
    }
    if (isViewport) {
      return placeholder
    }
    return defaultImg
  }, [ isLoaded, isViewport ])

  if (!src) {
    return (
      <img
        src={placeholder}
        width={width}
        height={height}
        className={className}
        alt={alt}
      />
    )
  }

  return (
    <ScrollTrigger onEnter={onEnter}>
      <img
        ref={imgRef}
        src={currentSrc}
        alt={alt}
        width={width}
        height={height}
        className={className}
      />
    </ScrollTrigger>
  )
}

export default ImageLoader
